import { Navigate, type RouteObject } from "react-router";
import { ROUTES } from "@/shared/constants/routes";

/**
 * The Rooms and Equipment workspace's routes (M27 W0-C-03, contracts §18), in a file of their own like
 * `coworkingRoutes.tsx` — `index.tsx` mounts this under the shell in one line.
 *
 * One layout route for the whole workspace: `RoomsWorkspaceGate` answers the add-on question once (the
 * entitlements, or any rooms read refused with `402 ADDON_REQUIRED`) and shows `AddonGate` in the page's place; after
 * a lapse it lets the readable pages through under the lapsed banner. Every page is a file in its owning lane's
 * folder (`src/modules/rooms/<area>/pages/`); a lane replaces the file and never edits this table. Static segments
 * come before the parameter beside them (`resources/new` before `resources/:resourceId`). `/rooms` itself has no
 * page: it is Overview. The wall display's public `/display` is not under this prefix and is mounted beside the other
 * public pages in `index.tsx`.
 */
export const roomsRoute: RouteObject = {
  path: "rooms",
  lazy: async () => ({
    Component: (await import("@/modules/rooms/shared/RoomsWorkspaceGate")).RoomsWorkspaceGate,
  }),
  children: [
    { index: true, element: <Navigate to={ROUTES.roomsOverview} replace /> },
    {
      path: "overview",
      handle: { title: "Overview" },
      lazy: async () => ({
        Component: (await import("@/modules/rooms/overview/pages/RoomsOverviewPage")).RoomsOverviewPage,
      }),
    },
    {
      // The calendar by default; `?view=list` is the list. Both are this one page.
      path: "bookings",
      handle: { title: "Bookings" },
      lazy: async () => ({
        Component: (await import("@/modules/rooms/bookings/pages/RoomsBookingsPage")).RoomsBookingsPage,
      }),
    },
    {
      // Keyed on the booking id and nothing else: no title, no host, no attendee ever sits in an address.
      path: "bookings/:bookingId",
      handle: { title: "Booking" },
      lazy: async () => ({
        Component: (await import("@/modules/rooms/bookings/pages/RoomsBookingPage")).RoomsBookingPage,
      }),
    },
    {
      path: "resources",
      handle: { title: "Resources" },
      lazy: async () => ({
        Component: (await import("@/modules/rooms/resources/pages/RoomsResourcesPage")).RoomsResourcesPage,
      }),
    },
    {
      // A static segment written BEFORE `:resourceId`, so the router can never read "new" as a resource.
      path: "resources/new",
      handle: { title: "New resource" },
      lazy: async () => ({
        Component: (await import("@/modules/rooms/resources/pages/RoomsResourceNewPage"))
          .RoomsResourceNewPage,
      }),
    },
    {
      path: "resources/:resourceId",
      handle: { title: "Resource" },
      lazy: async () => ({
        Component: (await import("@/modules/rooms/resources/pages/RoomsResourcePage")).RoomsResourcePage,
      }),
    },
    {
      // The module's own reports hub — never a card on the core one.
      path: "reports",
      handle: { title: "Rooms reports" },
      lazy: async () => ({
        Component: (await import("@/modules/rooms/reports/pages/RoomsReportsPage")).RoomsReportsPage,
      }),
    },
    {
      path: "reports/:kind",
      handle: { title: "Rooms report" },
      lazy: async () => ({
        Component: (await import("@/modules/rooms/reports/pages/RoomsReportPage")).RoomsReportPage,
      }),
    },
    {
      path: "displays",
      handle: { title: "Displays" },
      lazy: async () => ({
        Component: (await import("@/modules/rooms/displays/pages/RoomsDisplaysPage")).RoomsDisplaysPage,
      }),
    },
    {
      path: "settings",
      handle: { title: "Rooms settings" },
      lazy: async () => ({
        Component: (await import("@/modules/rooms/settings/pages/RoomsSettingsPage")).RoomsSettingsPage,
      }),
    },
    {
      // The setup guide (lane G): last, and `?step=` opens one step.
      path: "setup",
      handle: { title: "Setup guide" },
      lazy: async () => ({
        Component: (await import("@/modules/rooms/setup/pages/RoomsSetupPage")).RoomsSetupPage,
      }),
    },
  ],
};
